<article class="doc">
  <header class="doc__header">
    <p class="doc__eyebrow">Atom</p>
    <h1 class="doc__title">Rich Editor</h1>
    <p class="doc__lead">
      Editor de texto enriquecido basado en <strong>Quill</strong>, integrado sin wrapper Angular.
      Mismo patrón de inicialización que <code>ui-date-input</code> con Flatpickr.
      Toolbar completo: encabezados, negritas, cursiva, colores, listas, links, imágenes y bloques de código.
    </p>
  </header>

  <!-- ── Estados ────────────────────────────────── -->
  <h2 class="doc-section-title">Estados</h2>

  <div class="doc-stack">
    <app-example-section title="Normal" description="Editor activo con toolbar completo.">
      <ui-rich-editor
        label="Descripción"
        placeholder="Escribe el contenido aquí..."
        [showOptionalHint]="false"
      />
    </app-example-section>

    <app-example-section
      title="Error / inválido"
      description="Borde rojo y mensaje de error."
    >
      <div class="doc-example-stack">
        <ui-rich-editor
          label="Contenido del artículo"
          [required]="true"
          [invalid]="isInvalid()"
          message="Este campo es requerido."
          placeholder="Escribe algo..."
        />
        <button type="button" class="doc-native-toggle" (click)="toggleInvalid()">
          {{ isInvalid() ? 'Quitar error' : 'Activar error' }}
        </button>
      </div>
    </app-example-section>

    <app-example-section
      title="Deshabilitado"
      description="Toolbar y área de texto sin interacción."
    >
      <ui-rich-editor
        label="Notas"
        [value]="'<p>Contenido de solo lectura.</p>'"
        [disabled]="true"
        [showOptionalHint]="false"
      />
    </app-example-section>

    <app-example-section
      title="Solo lectura"
      description="Fondo gris, sin edición posible."
    >
      <ui-rich-editor
        label="Registro"
        [value]="'<p>Este contenido no es editable.</p>'"
        [readonly]="true"
        [showOptionalHint]="false"
      />
    </app-example-section>
  </div>

  <!-- ── Variantes ──────────────────────────────── -->
  <h2 class="doc-section-title">Variantes</h2>

  <div class="doc-stack">
    <app-example-section
      title="Altura personalizada"
      description="minHeight y maxHeight controlables."
    >
      <ui-rich-editor
        label="Comentario breve"
        placeholder="Máximo 6 líneas visibles..."
        minHeight="6rem"
        maxHeight="10rem"
        [showOptionalHint]="false"
      />
    </app-example-section>

    <app-example-section
      title="Obligatorio con binding"
      description="Binding bidireccional con signal."
    >
      <div class="doc-example-stack">
        <ui-rich-editor
          label="Descripción del producto"
          [(value)]="content"
          [required]="true"
          placeholder="Describe el producto..."
        />
        <p class="doc-value-preview">
          HTML capturado:
          <code class="doc-html-preview">{{ content() || '(vacío)' }}</code>
        </p>
      </div>
    </app-example-section>
  </div>

  <!-- ── Ejemplos de uso ────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Ejemplos de uso</h2>

    <p class="doc-block__p">Binding bidireccional básico:</p>
    <pre class="doc-block__pre" role="code"><code>content = signal('');

&lt;ui-rich-editor
  label="Descripción"
  [(value)]="content"
  [required]="true"
  placeholder="Escribe aquí..."
/&gt;</code></pre>

    <p class="doc-block__p">Altura controlada:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-rich-editor
  label="Resumen"
  [(value)]="summary"
  minHeight="8rem"
  maxHeight="20rem"
/&gt;</code></pre>

    <p class="doc-block__p">Con Reactive Forms y validación:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-rich-editor
  label="Contenido"
  [required]="true"
  [value]="form.controls.body.value ?? ''"
  (valueChange)="form.controls.body.setValue($event)"
  [invalid]="form.controls.body.invalid &amp;&amp; form.controls.body.touched"
  message="El contenido es requerido."
  [showMessage]="submitted()"
/&gt;</code></pre>
  </section>

  <!-- ── Inputs ─────────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Inputs</h2>
    <table class="doc-table">
      <thead>
        <tr><th>Nombre</th><th>Tipo</th><th>Default</th><th>Descripción</th></tr>
      </thead>
      <tbody>
        <tr><td><code>value</code></td><td><code>string</code> (model)</td><td><code>''</code></td><td>Contenido HTML del editor. Soporta <code>[(value)]</code>.</td></tr>
        <tr><td><code>label</code></td><td><code>string</code></td><td><code>''</code></td><td>Etiqueta sobre el editor.</td></tr>
        <tr><td><code>placeholder</code></td><td><code>string</code></td><td><code>'Escribe aquí...'</code></td><td>Texto de ayuda cuando está vacío.</td></tr>
        <tr><td><code>minHeight</code></td><td><code>string</code></td><td><code>'12rem'</code></td><td>Altura mínima del área editable.</td></tr>
        <tr><td><code>maxHeight</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Altura máxima, activa scroll interno.</td></tr>
        <tr><td><code>required</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Badge "Obligatorio" y <code>aria-required</code>.</td></tr>
        <tr><td><code>showOptionalHint</code></td><td><code>boolean</code></td><td><code>true</code></td><td>Badge "Opcional".</td></tr>
        <tr><td><code>disabled</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Deshabilita el editor y el toolbar.</td></tr>
        <tr><td><code>readonly</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Modo solo lectura.</td></tr>
        <tr><td><code>invalid</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Borde rojo.</td></tr>
        <tr><td><code>message</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Texto del mensaje de error.</td></tr>
        <tr><td><code>showMessage</code></td><td><code>boolean</code></td><td><code>true</code></td><td>Controla visibilidad del mensaje.</td></tr>
        <tr><td><code>block</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Ancho 100% del contenedor.</td></tr>
        <tr><td><code>fieldId</code></td><td><code>string | undefined</code></td><td>Auto-generado</td><td>ID del contenedor Quill.</td></tr>
      </tbody>
    </table>
  </section>

  <!-- ── Outputs ───────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Outputs</h2>
    <table class="doc-table">
      <thead>
        <tr><th>Nombre</th><th>Tipo</th><th>Descripción</th></tr>
      </thead>
      <tbody>
        <tr><td><code>valueChange</code></td><td><code>string</code></td><td>Emite el HTML completo en cada cambio.</td></tr>
        <tr><td><code>focused</code></td><td><code>void</code></td><td>Emite cuando el editor recibe el foco.</td></tr>
        <tr><td><code>blurred</code></td><td><code>void</code></td><td>Emite cuando el editor pierde el foco.</td></tr>
      </tbody>
    </table>
  </section>

  <!-- ── Buenas prácticas ──────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Buenas prácticas</h2>
    <ul class="doc-list">
      <li>El valor emitido es <strong>HTML</strong>. Siempre sanearlo antes de guardarlo o mostrarlo (<code>DomSanitizer</code> o backend).</li>
      <li>Define <code>maxHeight</code> en formularios para evitar que el editor empuje el resto del layout.</li>
      <li>Usa <code>[showMessage]="submitted()"</code> para mostrar el error solo después de un intento de envío.</li>
      <li>Para campos de solo visualización usa <code>[readonly]="true"</code>, no <code>[disabled]="true"</code>; la diferencia es semántica y visual.</li>
      <li>El valor vacío se normaliza como <code>''</code>; no compares contra <code>'&lt;p&gt;&lt;br&gt;&lt;/p&gt;'</code> en el componente padre.</li>
    </ul>
  </section>
</article>
